/*
  metaobject-article.js
  Hydrates the metaobject-article section: editorial tooltip, sticky table cells,
  sleep-expert blockquote injection, affiliate-sales card hydration, product slider,
  references show-more, sticky CTA. Each initializer no-ops if its DOM is absent.
*/

(function () {
  'use strict';

  /* ── Editorial-process tooltip ──────────────────────────────────────
     Toggles a click/keyboard-driven tooltip. Position is fixed by CSS
     (above-right of the icon); the JS only manages open/close state. */
  function initEditorialTooltip(root) {
    var icon = root.querySelector('[data-editorial-icon]');
    var blurb = root.querySelector('[data-editorial-blurb]');
    if (!icon || !blurb) return;

    blurb.removeAttribute('hidden');

    function open() {
      blurb.classList.add('is-open');
      icon.setAttribute('aria-expanded', 'true');
    }

    function close() {
      blurb.classList.remove('is-open');
      icon.setAttribute('aria-expanded', 'false');
    }

    icon.addEventListener('click', function (e) {
      e.stopPropagation();
      if (blurb.classList.contains('is-open')) {
        close();
      } else {
        open();
      }
    });

    icon.addEventListener('keydown', function (e) {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        if (blurb.classList.contains('is-open')) {
          close();
        } else {
          open();
        }
      } else if (e.key === 'Escape') {
        close();
      }
    });

    document.addEventListener('click', function (e) {
      if (!icon.contains(e.target) && !blurb.contains(e.target)) {
        close();
      }
    });
  }

  /* ── Sticky table first column ─────────────────────────────────────
     On horizontal scroll, the first column gets a shadow via .is-sticky. */
  function initStickyTableCells(root) {
    var tables = root.querySelectorAll('.table-container');
    tables.forEach(function (tableElem) {
      tableElem.addEventListener('scroll', function (e) {
        var stickyCells = tableElem.querySelectorAll('td:nth-child(1)');
        var scrolled = e.target.scrollLeft > 0;
        stickyCells.forEach(function (cell) {
          cell.classList.toggle('is-sticky', scrolled);
        });
      });
    });
  }

  /* ── Headerless table detection ───────────────────────────────────
     WP body tables ship no <thead>/<th>, so the CSS styles the first row
     as a header by default. That's wrong for key/value tables (e.g. a
     "factor | description" list) whose first row is just data. Tag those
     with .table--no-header so the CSS skips the header treatment.
     Conservative: only opt out when a first-row cell holds a long block of
     text — a real header's cells are short labels — so genuine header rows
     are never demoted. */
  function initTableHeaders(root) {
    var tables = root.querySelectorAll('.scrollable-table table');
    tables.forEach(function (table) {
      // Explicit header markup is authoritative — leave it alone.
      if (table.tHead || table.querySelector('th')) return;
      var firstRow = table.querySelector('tr');
      if (!firstRow) return;
      var cells = firstRow.children;
      if (cells.length < 2) return;
      for (var i = 0; i < cells.length; i++) {
        if ((cells[i].textContent || '').trim().length > 60) {
          table.classList.add('table--no-header');
          return;
        }
      }
    });
  }

  /* ── Sleep-expert blockquote hydration ─────────────────────────────
     Reads server-rendered JSON island; appends author block to any
     <blockquote data-expert-id> referencing a known expert. */
  function initSleepExpertQuotes(root) {
    var island = root.querySelector('[data-article-experts]');
    if (!island) return;
    var experts;
    try {
      experts = JSON.parse(island.textContent);
    } catch (err) {
      return;
    }
    var quotes = root.querySelectorAll('blockquote[data-expert-id]');
    quotes.forEach(function (quote) {
      var data = experts[quote.dataset.expertId];
      if (!data || quote.querySelector('.quote-author')) return;
      var author = document.createElement('div');
      author.className = 'quote-author';
      author.innerHTML =
        '<img src="' + (data.avatar || '') + '" alt="" height="40" width="40">' +
        '<span>' + (data.name || '') + '<br><small>' + (data.titles || '') + '</small></span>';
      quote.appendChild(author);
    });
  }

  /* ── Affiliate-sales card hydration ────────────────────────────────
     Replaces <div class="affiliate-sale-card" data-affiliate-id> innerHTML
     with the full card markup, sourced from the server-rendered island. */
  function initAffiliateCards(root) {
    var island = root.querySelector('[data-article-affiliates]');
    if (!island) return;
    var affiliates;
    try {
      affiliates = JSON.parse(island.textContent);
    } catch (err) {
      return;
    }
    var cards = root.querySelectorAll('div.affiliate-sale-card[data-affiliate-id]');
    cards.forEach(function (card) {
      var data = affiliates[card.dataset.affiliateId];
      if (!data) return;
      card.innerHTML =
        '<a href="' + (data.url || '#') + '" class="affiliate-link" target="_blank" rel="noopener">' +
          '<div>' +
            '<div class="affiliate-brand-logo">' +
              (data.logo ? '<img src="' + data.logo + '" alt="' + (data.name || '') + ' logo">' : '') +
            '</div>' +
            '<div class="affiliate-brand-name">' + (data.name || '') + '</div>' +
            '<div class="affiliate-discount-badge">' + (data.promo || '') + '</div>' +
            '<div class="affiliate-description">' + (data.description || '') + '</div>' +
          '</div>' +
          '<div>' +
            '<div class="affiliate-date-range">' + (data.date || '') + '</div>' +
            '<button type="button" class="btn btn--sm btn--blue btn--narrow">CLAIM NOW</button>' +
          '</div>' +
        '</a>';
    });
  }

  /* ── Product slider / overflow card ────────────────────────────────
     For each .overflow-card-slider in the body, fetches each linked
     product via /products/{handle}.js and renders a card. Wires up
     desktop arrow controls and mobile dot pagination. */
  function formatPrice(cents) {
    var dollars = (cents || 0) / 100;
    return dollars.toLocaleString('en-US', { style: 'currency', currency: 'USD' });
  }

  function initOverflowSlider(root) {
    var sliders = root.querySelectorAll('.overflow-card-slider');
    sliders.forEach(function (cards) {
      var slideContainer = cards.querySelector('.slide-container');
      var inner = cards.querySelector('.inner');
      var cardEls = cards.querySelectorAll('.slider-card');
      if (!slideContainer || !inner || !cardEls.length) return;

      var leftBtn = cards.querySelector('.dsk-card-btn[data-position="-"]');
      var rightBtn = cards.querySelector('.dsk-card-btn[data-position="+"]');
      var desktopBtns = cards.querySelector('.dsk-btn-container');
      var mobileBtns = cards.querySelector('.mbl-btn-container');
      var cellWidth = cardEls[0].offsetWidth;
      var routesRoot = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

      cardEls.forEach(function (cardElem) {
        if (!cardElem.dataset.handle) {
          cardElem.remove();
          return;
        }
        fetch(routesRoot + 'products/' + cardElem.dataset.handle + '.js')
          .then(function (response) {
            if (!response.ok) throw new Error('product not found: ' + cardElem.dataset.handle);
            return response.json();
          })
          .then(function (product) {
            cardElem.href = product.url;
            var priceMarkup = product.compare_at_price
              ? '<span>' + formatPrice(product.price) + '</span><s>' + formatPrice(product.compare_at_price) + '</s>'
              : '<span>' + formatPrice(product.price) + '</span>';
            cardElem.innerHTML =
              '<figure>' +
                '<img src="' + product.featured_image + '" alt="" loading="lazy" height="200" width="200">' +
              '</figure>' +
              '<div class="btm-content">' +
                '<p class="vendor">' + (product.vendor || '') + '</p>' +
                '<p class="title">' + (product.title || '') + '</p>' +
                '<p class="price">' + priceMarkup + '</p>' +
                '<button type="button" class="btn btn--sm btn--blue btn--narrow">ADD TO CART</button>' +
              '</div>';
            cellWidth = cardEls[0].offsetWidth;
          })
          .catch(function () {
            cardElem.remove();
          });
      });

      slideContainer.addEventListener('scroll', function () {
        var width = slideContainer.offsetWidth;
        var scrollWidth = slideContainer.scrollWidth;
        var scrollLeft = slideContainer.scrollLeft;
        var position = (scrollLeft / (scrollWidth - width)) * 100;

        if (mobileBtns) {
          Array.prototype.forEach.call(mobileBtns.children, function (el) {
            el.classList.remove('active');
          });
        }

        if (position === 0) {
          if (leftBtn) leftBtn.classList.add('innactive');
          if (rightBtn) rightBtn.classList.remove('innactive');
        } else if (position > 99) {
          if (rightBtn) rightBtn.classList.add('innactive');
          if (leftBtn) leftBtn.classList.remove('innactive');
        } else {
          if (leftBtn) leftBtn.classList.remove('innactive');
          if (rightBtn) rightBtn.classList.remove('innactive');
        }
      });

      if (desktopBtns) {
        desktopBtns.addEventListener('click', function (e) {
          var pos = e.target.getAttribute('data-position');
          if (!pos) return;
          slideContainer.scrollBy({
            top: 0,
            left: pos === '+' ? cellWidth + 16 : -(cellWidth + 16),
            behavior: 'smooth'
          });
        });
      }

      if (mobileBtns) {
        mobileBtns.addEventListener('click', function (e) {
          var pos = e.target.getAttribute('data-position');
          if (!pos) return;
          slideContainer.scrollTo({
            top: 0,
            left: cellWidth * Number(pos),
            behavior: 'smooth'
          });
        });
      }
    });
  }

  /* ── Sticky CTA visibility ─────────────────────────────────────────
     Becomes visible once the user has scrolled past either:
       - a custom trigger element (data-custom-url starts with #), or
       - the article wrapper (data-trigger-id), or
       - a scroll percentage threshold (data-scroll-percentage > 0).
     Re-hides when the footer enters the viewport. */
  function initStickyCta(root) {
    var section = root.closest('.shopify-section') || document;
    var stickyCta = section.querySelector('[data-sticky-cta]');
    if (!stickyCta) return;

    stickyCta.removeAttribute('hidden');

    var scrollPercentage = parseInt(stickyCta.getAttribute('data-scroll-percentage'), 10) || 0;
    var useScrollPercentage = scrollPercentage > 0;
    var useCustomUrl = stickyCta.getAttribute('data-use-custom-url') === 'true';
    var customUrl = stickyCta.getAttribute('data-custom-url') || '';
    var triggerId = stickyCta.getAttribute('data-trigger-id') || '';

    var triggerElement = null;
    if (!useScrollPercentage) {
      if (useCustomUrl && customUrl && customUrl.charAt(0) === '#') {
        triggerElement = document.getElementById(customUrl.substring(1));
      }
      if (!triggerElement && triggerId) {
        var article = document.getElementById(triggerId);
        if (article) {
          triggerElement = article.querySelector('h1') || article;
        }
      }
      if (!triggerElement) return;
    }

    var footer = document.querySelector('footer, [role="contentinfo"], .footer, #shopify-section-footer');
    var hasScrolledPastTrigger = false;
    var footerVisible = false;

    function updateVisibility() {
      var show = hasScrolledPastTrigger && !footerVisible;
      stickyCta.classList.toggle('is-visible', show);
    }

    function checkScrollPosition() {
      var scrolledPast = false;
      if (useScrollPercentage) {
        var scrollTop = window.pageYOffset || document.documentElement.scrollTop;
        var docHeight = document.documentElement.scrollHeight - document.documentElement.clientHeight;
        scrolledPast = (scrollTop / docHeight) * 100 >= scrollPercentage;
      } else if (triggerElement) {
        scrolledPast = triggerElement.getBoundingClientRect().bottom < 100;
      }
      if (hasScrolledPastTrigger !== scrolledPast) {
        hasScrolledPastTrigger = scrolledPast;
        updateVisibility();
      }
    }

    var rafId = null;
    window.addEventListener('scroll', function () {
      if (rafId) window.cancelAnimationFrame(rafId);
      rafId = window.requestAnimationFrame(checkScrollPosition);
    }, { passive: true });

    checkScrollPosition();

    if (footer && 'IntersectionObserver' in window) {
      var observer = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          footerVisible = entry.isIntersecting;
          updateVisibility();
        });
      }, { threshold: 0, rootMargin: '0px' });
      observer.observe(footer);
    }
  }

  /* ── Table of contents ─────────────────────────────────────────────
     Auto-populates from H2 elements in the article body, sets up
     scroll-spy for the active link, smooth-scrolls on click, and
     toggles `is-bottom` when the footer enters the viewport. */
  function initTableOfContents(root) {
    var section = root.closest('.shopify-section') || document;
    var toc = section.querySelector('[data-toc]');
    if (!toc) return;

    var contentSelector = toc.getAttribute('data-content-selector') || '.metaobject-article__body';
    var introId = toc.getAttribute('data-intro-id') || 'article_intro';
    var content = document.querySelector(contentSelector);
    var list = toc.querySelector('[data-toc-list]');
    if (!content || !list) return;

    var headings = content.querySelectorAll('h2');
    if (headings.length === 0) return;

    Array.prototype.forEach.call(headings, function (heading) {
      if (!heading.id) {
        heading.id = heading.textContent
          .toLowerCase()
          .replace(/[^a-z0-9]+/g, '-')
          .replace(/(^-|-$)/g, '');
      }
      var li = document.createElement('li');
      li.className = 'metaobject-article__toc-item';
      var a = document.createElement('a');
      a.className = 'metaobject-article__toc-link';
      a.href = '#' + heading.id;
      a.textContent = heading.textContent;
      li.appendChild(a);
      list.appendChild(li);
    });

    toc.removeAttribute('hidden');

    /* Only include the height of layout chrome that is actually fixed/sticky.
       The site header on this theme is not sticky, so subtracting its height
       would push every TOC link target an extra ~80px deeper than it should. */
    function fixedHeight(el) {
      if (!el) return 0;
      var pos = window.getComputedStyle(el).position;
      return (pos === 'fixed' || pos === 'sticky') ? el.offsetHeight : 0;
    }

    function getOffset() {
      var offset = 30;
      offset += fixedHeight(document.querySelector('sticky-header, .header, header'));
      offset += fixedHeight(document.querySelector('.announcement-bar, .sleepdoctor-announcement-bar'));
      return offset;
    }

    var links = toc.querySelectorAll('.metaobject-article__toc-link');

    Array.prototype.forEach.call(links, function (link) {
      link.addEventListener('click', function (e) {
        var targetId = link.getAttribute('href');
        var target = document.querySelector(targetId);
        if (!target) return;
        e.preventDefault();
        window.scrollTo({
          top: target.offsetTop - getOffset(),
          behavior: 'smooth'
        });
      });
    });

    function buildSections() {
      var sections = [];
      var introEl = document.getElementById(introId);
      if (introEl) sections.push({ id: introId, top: introEl.offsetTop });
      Array.prototype.forEach.call(headings, function (h) {
        sections.push({ id: h.id, top: h.offsetTop });
      });
      return sections.sort(function (a, b) { return a.top - b.top; });
    }

    function updateActive() {
      var fromTop = window.scrollY + getOffset() + 50;
      var sections = buildSections();
      var current = null;
      for (var i = sections.length - 1; i >= 0; i--) {
        if (sections[i].top <= fromTop) { current = sections[i]; break; }
      }
      if (!current && sections.length) current = sections[0];
      Array.prototype.forEach.call(links, function (link) {
        link.classList.toggle('is-active', current && link.hash === '#' + current.id);
      });
    }

    var footer = document.querySelector('footer, [role="contentinfo"], .footer, #shopify-section-footer');

    /* Base top tracks the sticky site header (+ announcement bar) so the TOC
       sits `headerGap` below it instead of butting up against it. Mirrors the
       best-product TOC's `getBaseHeaderHeight` for parity between the two
       sections. The static CSS `top` is only the pre-JS fallback. */
    var stickyHeader = document.querySelector('sticky-header, .header, header');
    var announcementBar = document.querySelector('.announcement-bar, .sleepdoctor-announcement-bar');
    var headerGap = 20;
    var footerGap = 20;

    function getBaseTop() {
      var top = headerGap;
      if (stickyHeader) top += stickyHeader.offsetHeight;
      if (announcementBar) top += announcementBar.offsetHeight;
      return top;
    }

    /* Pin the TOC `headerGap` below the header. Once the footer enters the
       viewport, shift `top` up so the TOC's bottom edge stays `footerGap`
       above the footer — the panel scrolls up with the page rather than
       resizing. */
    function updateLayout() {
      toc.style.maxHeight = '';
      var topPx = getBaseTop();
      if (footer) {
        var footerRect = footer.getBoundingClientRect();
        var maxTopForFooter = footerRect.top - toc.offsetHeight - footerGap;
        if (maxTopForFooter < topPx) topPx = maxTopForFooter;
      }
      toc.style.top = topPx + 'px';
    }

    var ticking = false;
    window.addEventListener('scroll', function () {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(function () {
        updateActive();
        updateLayout();
        ticking = false;
      });
    }, { passive: true });

    window.addEventListener('resize', function () {
      window.requestAnimationFrame(function () {
        updateActive();
        updateLayout();
      });
    }, { passive: true });

    updateActive();
    updateLayout();
  }

  /* ── References show-more / show-less toggle ───────────────────────
     Populates the inline count from <ol> li.length and toggles
     `.is-expanded` on the references content + the button's
     aria-expanded / label. */
  function initReferencesToggle(root) {
    var button = root.querySelector('[data-references-toggle]');
    var content = root.querySelector('[data-references-content]');
    var label = root.querySelector('[data-references-label]');
    var count = root.querySelector('[data-references-count]');
    if (!button || !content) return;

    if (count) {
      var items = content.querySelectorAll('ol > li');
      if (items.length > 0) {
        count.textContent = '(' + items.length + ')';
      }
    }

    button.addEventListener('click', function () {
      var expanded = content.classList.toggle('is-expanded');
      button.setAttribute('aria-expanded', expanded ? 'true' : 'false');
      if (label) label.textContent = expanded ? 'See Less' : 'See More';
    });
  }

  function init(root) {
    if (!root) return;
    initEditorialTooltip(root);
    initStickyTableCells(root);
    initTableHeaders(root);
    initSleepExpertQuotes(root);
    initAffiliateCards(root);
    initOverflowSlider(root);
    initReferencesToggle(root);
    initStickyCta(root);
    initTableOfContents(root);
  }

  function bootstrap() {
    document.querySelectorAll('[data-section-type="metaobject-article"]').forEach(init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bootstrap);
  } else {
    bootstrap();
  }

  document.addEventListener('shopify:section:load', function (event) {
    var root = event.target.querySelector('[data-section-type="metaobject-article"]');
    if (root) init(root);
  });
})();
